import React, {useContext, useEffect, useRef, useState} from 'react';
import {HeaderProps} from '../types/components';
import {AppContext} from '../context/AppContext';
import {Util} from '../services/Util';

// Icon components
const TimerIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path
      d="M15 1H9v2h6V1zm-4 13h2V8h-2v6zm8.03-6.61l1.42-1.42c-.43-.51-.9-.99-1.41-1.41l-1.42 1.42A8.962 8.962 0 0012 4c-4.97 0-9 4.03-9 9s4.02 9 9 9a8.994 8.994 0 007.03-14.61zM12 20c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/>
  </svg>
);

const SettingsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path
      d="M19.14 12.94c.04-.3.06-.61.06-.94 0-.32-.02-.64-.07-.94l2.03-1.58a.49.49 0 00.12-.61l-1.92-3.32a.488.488 0 00-.59-.22l-2.39.96c-.5-.38-1.03-.7-1.62-.94l-.36-2.54a.484.484 0 00-.48-.41h-3.84c-.24 0-.43.17-.47.41l-.36 2.54c-.59.24-1.13.57-1.62.94l-2.39-.96c-.22-.08-.47 0-.59.22L2.74 8.87c-.12.21-.08.47.12.61l2.03 1.58c-.05.3-.09.63-.09.94s.02.64.07.94l-2.03 1.58a.49.49 0 00-.12.61l1.92 3.32c.12.22.37.29.59.22l2.39-.96c.5.38 1.03.7 1.62.94l.36 2.54c.05.24.24.41.48.41h3.84c.24 0 .44-.17.47-.41l.36-2.54c.59-.24 1.13-.56 1.62-.94l2.39.96c.22.08.47 0 .59-.22l1.92-3.32c.12-.22.07-.47-.12-.61l-2.01-1.58zM12 15.6c-1.98 0-3.6-1.62-3.6-3.6s1.62-3.6 3.6-3.6 3.6 1.62 3.6 3.6-1.62 3.6-3.6 3.6z"/>
  </svg>
);

const DownloadsIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path d="M19 9h-4V3H9v6H5l7 7 7-7zM5 18v2h14v-2H5z"/>
  </svg>
);

const SyncIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    <path
      d="M12 4V1L8 5l4 4V6c3.31 0 6 2.69 6 6 0 1.01-.25 1.97-.7 2.8l1.46 1.46A7.93 7.93 0 0020 12c0-4.42-3.58-8-8-8zm0 14c-3.31 0-6-2.69-6-6 0-1.01.25-1.97.7-2.8L5.24 7.74A7.93 7.93 0 004 12c0 4.42 3.58 8 8 8v3l4-4-4-4v3z"/>
  </svg>
);

const ChevronDownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M7 10l5 5 5-5z"/>
  </svg>
);

const Header: React.FC<HeaderProps> = ({currentView, onViewChange}) => {
  const context = useContext(AppContext);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  if (!context) {
    throw new Error('Header must be used within AppContextProvider');
  }

  const {
    i18n,
    // settings, setAutoSync, syncQueue
  } = context;

  // const handleSync = async () => {
  //   await syncQueue.process();
  // };

  // const handleAutoSyncToggle = async (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const isChecked = e.target.checked;
  //   await setAutoSync(isChecked);
  // };

  const hasElectronAPI = Boolean(window?.electronAPI);
  const isBrowserExtension = Util.isBrowserExtension();

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownOpen]);

  const getCurrentViewIcon = () => {
    switch (currentView) {
      case 'timer':
        return <TimerIcon/>;
      case 'settings':
        return <SettingsIcon/>;
      case 'downloads':
        return <DownloadsIcon/>;
      default:
        return <TimerIcon/>;
    }
  };

  const handleViewChange = (view: 'timer' | 'settings' | 'downloads') => {
    onViewChange(view);
    setDropdownOpen(false);
  };

  return (
    <header className="app-header">
      <div>
        <div className="brand clickable" onClick={() => onViewChange('timer')}>
          <span className="logo">
            <img src="logo.svg" alt="SNK Logo"/>
          </span>
          <h1>Falcon</h1>
        </div>
        <div className="controls">
          {/* <button
            id="sync-btn"
            className="btn primary sync-btn"
            title={i18n.t('sync')}
            onClick={handleSync}
          >
            <span className="sync-btn-text">{i18n.t('sync')}</span>
            <span className="sync-btn-icon"><SyncIcon/></span>
          </button> */}

          {/* Desktop: Auto-sync toggle */}
          {/* <div className="auto-sync-desktop">
            <label className="switch" title={i18n.t('autoSync')}>
              <input
                type="checkbox"
                id="auto-sync-toggle"
                checked={settings.autoSync}
                onChange={handleAutoSyncToggle}
              />
              <span className="slider"></span>
            </label>
            <span className="switch-label">{i18n.t('autoSync')}</span>
          </div> */}

          {/* Desktop: Regular navigation */}
          <nav className="nav nav-desktop">
            <button
              id="nav-timer"
              className={`tab ${currentView === 'timer' ? 'active' : ''}`}
              onClick={() => onViewChange('timer')}
            >
              {i18n.t('timerView')}
            </button>
            <button
              id="nav-settings"
              className={`tab ${currentView === 'settings' ? 'active' : ''}`}
              onClick={() => onViewChange('settings')}
            >
              {i18n.t('settingsView')}
            </button>
            {(!hasElectronAPI && !isBrowserExtension) && (
              <button
                id="nav-downloads"
                className={`tab ${currentView === 'downloads' ? 'active' : ''}`}
                onClick={() => onViewChange('downloads')}
              >
                {i18n.t('downloads')}
              </button>
            )}
          </nav>

          {/* Mobile: Dropdown navigation */}
          <div className="nav-mobile-dropdown" ref={dropdownRef}>
            <button
              className="dropdown-trigger btn"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              aria-expanded={dropdownOpen}
            >
              {getCurrentViewIcon()}
              <ChevronDownIcon/>
            </button>

            {dropdownOpen && (
              <div className="dropdown-menu">
                <button
                  className={`dropdown-item ${currentView === 'timer' ? 'active' : ''}`}
                  onClick={() => handleViewChange('timer')}
                >
                  <TimerIcon/>
                  <span>{i18n.t('timerView')}</span>
                </button>
                <button
                  className={`dropdown-item ${currentView === 'settings' ? 'active' : ''}`}
                  onClick={() => handleViewChange('settings')}
                >
                  <SettingsIcon/>
                  <span>{i18n.t('settingsView')}</span>
                </button>
                {(!hasElectronAPI && !isBrowserExtension) && (
                  <button
                    className={`dropdown-item ${currentView === 'downloads' ? 'active' : ''}`}
                    onClick={() => handleViewChange('downloads')}
                  >
                    <DownloadsIcon/>
                    <span>{i18n.t('downloads')}</span>
                  </button>
                )}

                {/* <div className="dropdown-divider"></div> */}

                {/* <div className="dropdown-auto-sync">
                  <label className="switch" title={i18n.t('autoSync')}>
                    <input
                      type="checkbox"
                      checked={settings.autoSync}
                      onChange={handleAutoSyncToggle}
                    />
                    <span className="slider"></span>
                  </label>
                  <span className="switch-label">{i18n.t('autoSync')}</span>
                </div> */}
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;